import { secureGet, securePost, securePut } from '../utils/HttpUtils';

const apiUrls = {
  annexes: '/v1/private/annexes',
};

export interface AnnexItem {
  id?: number;
  name: string;
  requires_year: boolean;
  requires_month: boolean;
  query: string;
  created_at?: string;
}

export interface CreateAnnexRequest {
  name: string;
  requires_year: boolean;
  requires_month: boolean;
  query: string;
}

export interface AnnexesResponse {
  content: AnnexItem[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
}

export const getAnnexes = async (
  page: number = 0,
  size: number = 10,
  searchText: string = ''
) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (searchText.trim()) {
      params.append('filterText', searchText.trim());
    }

    const url = `${apiUrls.annexes}?${params.toString()}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching annexes:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getAnnex = async (id: string) => {
  try {
    const url = `${apiUrls.annexes}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching annex:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createAnnex = async (annexData: CreateAnnexRequest) => {
  try {
    return securePost(apiUrls.annexes, annexData);
  } catch (error) {
    console.error('Error creating annex:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateAnnex = async (id: string, annexData: CreateAnnexRequest) => {
  try {
    const url = `${apiUrls.annexes}/${id}`;
    return securePut(url, annexData);
  } catch (error) {
    console.error('Error updating annex:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const executeAnnex = async (id: number, year?: number, month?: number, annexName?: string) => {
  try {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    
    const url = `${apiUrls.annexes}/${id}/execute${params.toString() ? '?' + params.toString() : ''}`;
    
    // Para descarga de CSV, usamos fetch directamente para manejar la respuesta como blob
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const yyyymmdd = `${yyyy}${mm}${dd}`;
    const sanitizedAnnexName = (annexName || `ANNEX_${id}`)
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .toUpperCase();
    let suggestedFileName = `${sanitizedAnnexName}_${yyyymmdd}.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";]+)"?/i);
      if (match && match[1]) {
        suggestedFileName = match[1];
      }
    }

    const blob = await response.blob();
    
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = suggestedFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  } catch (error) {
    console.error('Error executing annex:', JSON.stringify(error, null, 2));
    throw error;
  }
};